import { ReactNode, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from '@mui/material'
import { Fund } from '../../api/types'
import { useOpsApi } from '../../api/context'
import { apiError } from '../../api/errors'
import { totalUnitsImpact, valuationUnitPrice } from '../../calc/preview'
import { Dec } from '../../calc/dec'
import { formatCzk, formatDate, formatPercent, formatUnits } from './format'

/** Jednotky jsou numeric(28,8) — víc desetinných míst BE stejně zahodí. */
const MAX_UNIT_DECIMALS = 8

/** Nad tenhle poměr nové/staré hodnoty jde o řádový rozdíl (typicky překlep v nulách). */
const RADOVY_ROZDIL = 10

/** Od téhle změny ceny jednotky se hlásí varování (10 %). */
const VELKA_ZMENA_CENY = 0.1

/**
 * Text → PŘESNÝ desítkový zápis počtu jednotek. Mezery (i pevné) pryč, čárka na tečku.
 * Vrací null, když to není číslo — hodnota se nikdy neprožene přes `Number`, aby se
 * u desítek milionů jednotek s 8 desetinnými místy nic nezaokrouhlilo.
 */
function parseUnitsText(raw: string): string | null {
  const cleaned = raw.replace(/[\s\u00a0]/g, '').replace(',', '.')
  return /^-?\d+(\.\d+)?$/.test(cleaned) ? cleaned : null
}

/**
 * Skupinové oddělení PŘESNÉHO desítkového zápisu (cs-CZ: nedělitelná mezera + čárka).
 * Nepouští hodnotu přes `Number`, takže se v potvrzení ukáže přesně to, co se uloží.
 */
function formatUnitsExact(decimal: string): string {
  const negative = decimal.charAt(0) === '-'
  const abs = negative ? decimal.slice(1) : decimal
  const dot = abs.indexOf('.')
  const intPart = dot >= 0 ? abs.slice(0, dot) : abs
  const frac = dot >= 0 ? abs.slice(dot + 1).replace(/0+$/, '') : ''
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
  return `${negative ? '\u2212' : ''}${grouped}${frac ? ',' + frac : ''}`
}

/** Procenta se znaménkem — „+2,1 %" / „−2,1 %" (formatPercent plus sám netiskne). */
function formatSignedPercent(ratio: number): string {
  return ratio > 0 ? `+${formatPercent(ratio)}` : formatPercent(ratio)
}

interface TotalUnitsDialogProps {
  open: boolean
  fund: Fund
  /** Voláno po uloženi s fondem, který vrátil BE (rodič přenačte data a upozorní obsluhu). */
  onSaved: (fund: Fund) => void
  onClose: () => void
}

/**
 * Ruční oprava celkového počtu jednotek fondu (jen CEO). total_units je jmenovatel ceny
 * jednotky, takže překlep přecení podíl všem klientům fondu — dialog proto vedle nového
 * čísla vždy ukazuje, co to udělá s cenou (ze stejného NAV posledního ocenění), hlásí
 * řádový rozdíl, vyžaduje důvod a ukládá až na druhé potvrzení (vzor ConfirmBatchDialog).
 */
export function TotalUnitsDialog({ open, fund, onSaved, onClose }: TotalUnitsDialogProps) {
  const api = useOpsApi()

  const [text, setText] = useState('')
  const [note, setNote] = useState('')
  const [attempted, setAttempted] = useState(false)
  const [secondStep, setSecondStep] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset při každém otevření — dialog se nesmí otevřít s rozepsanou hodnotou z minula.
  useEffect(() => {
    if (open) {
      setText('')
      setNote('')
      setAttempted(false)
      setSecondStep(false)
      setSubmitting(false)
      setError(null)
    }
  }, [open])

  const parsed = parseUnitsText(text)

  const valueError = ((): string | null => {
    if (!text.trim()) return 'Zadejte nový počet jednotek'
    if (parsed === null) return 'Zadejte počet jednotek číslem'
    if (parsed.charAt(0) === '-') return 'Počet jednotek nesmí být záporný'
    const decimals = parsed.indexOf('.') >= 0 ? parsed.length - parsed.indexOf('.') - 1 : 0
    if (decimals > MAX_UNIT_DECIMALS) return `Nejvýše ${MAX_UNIT_DECIMALS} desetinných míst`
    if (Dec.parse(parsed).cmp(Dec.of(fund.totalUnits)) === 0) {
      return 'Zadaná hodnota se rovná současnému počtu jednotek'
    }
    return null
  })()

  const noteError = note.trim() ? null : 'Zadejte důvod opravy — ukládá se do auditu'

  /**
   * Cena „teď" = NAV posledního ocenění / SOUČASNÝ počet jednotek. Nemusí se rovnat ceně
   * uložené v ocenění: po každé ruční opravě si ocenění drží svou původní cenu, dokud
   * nevznikne nové. Rozdíl se proto vypisuje — je to přesně ta věc, na kterou se zapomíná.
   */
  const priceNow = fund.lastValuation
    ? valuationUnitPrice(fund.lastValuation.navTotal, fund.totalUnits, 0)
    : null
  const storedPriceDiffers =
    fund.lastValuation !== undefined &&
    priceNow !== null &&
    priceNow > 0 &&
    Math.abs(priceNow - fund.lastValuation.unitPrice) > 1e-8

  // Dopad na cenu jde spočítat, jen když fond má ocenění (NAV) a hodnota je validní.
  const impact =
    parsed !== null && valueError === null && fund.lastValuation
      ? totalUnitsImpact(fund.lastValuation.navTotal, fund.totalUnits, parsed)
      : null

  const radovyRozdil =
    impact !== null &&
    impact.unitsRatio !== null &&
    (impact.unitsRatio >= RADOVY_ROZDIL || impact.unitsRatio <= 1 / RADOVY_ROZDIL)
  const velkaZmenaCeny =
    impact !== null && impact.priceChange !== null && Math.abs(impact.priceChange) >= VELKA_ZMENA_CENY

  // Jakákoli změna vstupů shazuje druhý krok — potvrzuje se vždy to, co je zrovna vidět.
  const handleTextChange = (value: string) => {
    setText(value)
    setSecondStep(false)
    setError(null)
  }
  const handleNoteChange = (value: string) => {
    setNote(value)
    setSecondStep(false)
  }

  const handleSubmit = async () => {
    if (submitting) return
    setAttempted(true)
    if (valueError || noteError || parsed === null) return
    if (!secondStep) {
      setSecondStep(true)
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const saved = await api.setFundTotalUnits(fund.key, { totalUnits: parsed, note: note.trim() })
      onSaved(saved)
    } catch (err) {
      setError(apiError(err, 'Uložení počtu jednotek selhalo'))
      // Po odmítnutí zpět na první krok — oprava se nesmí dát „proklikat" na podruhé.
      setSecondStep(false)
    } finally {
      setSubmitting(false)
    }
  }

  const showValueError = (attempted || text.trim() !== '') && Boolean(valueError)

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Ruční oprava celkového počtu jednotek — {fund.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
          <Alert severity="info" variant="outlined">
            Cena jednotky = NAV / celkový počet jednotek. Tímhle číslem se přecení podíl
            <strong> všech klientů fondu</strong>, ne jen nových vkladů.
          </Alert>

          <TextField
            size="small"
            label="Nový celkový počet jednotek"
            value={text}
            onChange={(e) => handleTextChange(e.target.value)}
            error={showValueError}
            helperText={
              showValueError
                ? valueError
                : `Současný stav: ${formatUnits(fund.totalUnits)}. Desetinná místa čárkou nebo tečkou (max ${MAX_UNIT_DECIMALS}).`
            }
            placeholder={formatUnits(fund.totalUnits)}
            disabled={submitting}
            autoComplete="off"
          />

          <Box
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1.5,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}
          >
            <PreviewRow label="Celkem jednotek teď" value={formatUnits(fund.totalUnits)} />
            <PreviewRow
              label="Nově"
              value={
                parsed !== null && valueError === null ? (
                  <>
                    {formatUnitsExact(parsed)}
                    {impact && impact.unitsRatio !== null && (
                      <Typography
                        component="span"
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 1 }}
                      >
                        ({formatSignedPercent(impact.unitsRatio - 1)})
                      </Typography>
                    )}
                  </>
                ) : (
                  '—'
                )
              }
              strong
            />

            <Divider sx={{ my: 0.5 }} />

            {fund.lastValuation ? (
              <>
                <PreviewRow
                  label={`NAV z ocenění ${formatDate(fund.lastValuation.date)}`}
                  value={formatCzk(fund.lastValuation.navTotal)}
                />
                <PreviewRow
                  label="Cena jednotky teď"
                  value={
                    priceNow !== null && priceNow > 0 ? (
                      <>
                        {formatUnits(priceNow)}
                        {storedPriceDiffers && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ ml: 1 }}
                          >
                            (uložené ocenění má {formatUnits(fund.lastValuation.unitPrice)})
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography component="span" variant="body2" color="text.secondary">
                        — fond zatím nemá jednotky
                      </Typography>
                    )
                  }
                />
                <PreviewRow
                  label="Cena jednotky po opravě"
                  value={
                    impact && impact.priceAfter > 0 ? (
                      <>
                        {formatUnits(impact.priceAfter)}
                        {impact.priceChange !== null && (
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              ml: 1,
                              color: impact.priceChange < 0 ? 'error.main' : 'success.main',
                            }}
                          >
                            ({formatSignedPercent(impact.priceChange)})
                          </Typography>
                        )}
                      </>
                    ) : (
                      '—'
                    )
                  }
                  strong
                />
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Fond zatím nemá ocenění — dopad na cenu jednotky nelze spočítat. Cena se dopočte
                až z prvního ocenění (NAV).
              </Typography>
            )}
          </Box>

          {radovyRozdil && impact && impact.unitsRatio !== null && (
            <Alert severity="error">
              Řádový rozdíl proti současnému stavu:{' '}
              {impact.unitsRatio >= RADOVY_ROZDIL
                ? `nový počet je ${formatUnits(Math.round(impact.unitsRatio * 100) / 100)}× vyšší`
                : `nový počet je jen ${formatPercent(impact.unitsRatio)} současného`}
              . Zkontroluj počet nul a desetinnou čárku.
            </Alert>
          )}

          {velkaZmenaCeny && impact && impact.priceChange !== null && (
            <Alert severity="warning">
              Cena jednotky se změní o {formatSignedPercent(impact.priceChange)} — o tolik se
              posune hodnota podílu každého klienta fondu.
            </Alert>
          )}

          <TextField
            size="small"
            label="Důvod opravy (povinné)"
            value={note}
            onChange={(e) => handleNoteChange(e.target.value)}
            error={attempted && Boolean(noteError)}
            helperText={attempted && noteError ? noteError : 'Uloží se do auditu vedle hodnot před a po.'}
            placeholder="Např. zanesení jednotek upsaných mimo modul 2024–2026 dle Excelu."
            multiline
            minRows={2}
            disabled={submitting}
          />

          {secondStep && (
            <Alert severity="error" icon={false}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Opravdu? Tahle akce přecení podíl všech klientů fondu {fund.name}.
              </Typography>
              <Typography variant="body2">
                Jednotky {formatUnits(fund.totalUnits)} →{' '}
                {parsed !== null ? formatUnitsExact(parsed) : '—'}
                {impact && impact.priceAfter > 0 && impact.priceChange !== null && (
                  <>
                    , cena jednotky {formatUnits(impact.priceBefore)} →{' '}
                    {formatUnits(impact.priceAfter)} ({formatSignedPercent(impact.priceChange)})
                  </>
                )}
                .
              </Typography>
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button variant="text" color="inherit" onClick={onClose} disabled={submitting}>
          Zrušit
        </Button>
        <Button
          variant="contained"
          color={secondStep ? 'error' : 'primary'}
          onClick={handleSubmit}
          disabled={submitting}
        >
          {secondStep ? 'Ano, opravdu uložit' : 'Uložit počet jednotek'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function PreviewRow({
  label,
  value,
  strong = false,
}: {
  label: string
  value: ReactNode
  strong?: boolean
}) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: strong ? 600 : 400, textAlign: 'right' }}>
        {value}
      </Typography>
    </Box>
  )
}
